/**
 * Adawat scroll reveal — sections + items fade/slide up on scroll
 * Product cards & category circles animate together (no one-by-one stagger)
 */
(function () {
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (REDUCE.matches) {
    document.documentElement.classList.remove('adawat-sr');
    return;
  }

  document.documentElement.classList.add('adawat-sr');

  const ROOT_MARGIN = '0px 0px -10% 0px';
  const ITEM_STAGGER = 90;
  const MAX_STAGGER_ITEMS = 16;

  // Animate as a group (same delay, revealed together)
  const GROUP_SELECTORS = [
    '.adawat-product-card',
    '.adawat-category-card',
    '.adawat-category-grid__item',
    '.adawat-categories .adawat-category-card',
    '.adawat-categories__scroll > *',
  ].join(',');

  // Whole section only — no per-card / one-by-one reveal (Adawat's On Fire)
  const SECTION_WHOLE_SELECTORS = [
    '.adawat-shoppable-video',
    '.adawat-section-shoppable-video',
    '.section-adawat-shoppable-video',
  ].join(',');

  // Staggered items (not product cards / category rows / On Fire videos)
  const ITEM_SELECTORS = [
    '.adawat-featured-look',
    '.adawat-fc__label',
    '.adawat-fc__tabs',
    '.adawat-fc__thumbs',
    '.adawat-fc__description',
    '.adawat-fc__cta',
    '.adawat-fc__media',
    '.adawat-fc__intro',
    '.adawat-fc__detail',
    '.adawat-fav',
    '.adawat-flash-sale',
    '.adawat-promo-card',
    '.adawat-promotional-cards__card',
    '.adawat-promotional-cards__item',
    '.adawat-promo-slide',
    '.adawat-brand-card',
    '.adawat-trust-bar__item',
    '.adawat-trust__item',
    '.adawat-carousel__item',
    '.adawat-product-promo',
    '.adawat-subcat-carousel__item',
    '.adawat-featured-looks__title',
    '.adawat-category-grid__header',
    '.adawat-section__header',
    '.adawat-carousel__header',
    '[data-adawat-reveal]',
  ].join(',');

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function isOnFireSection(section) {
    return Boolean(
      section?.matches?.(SECTION_WHOLE_SELECTORS) || section?.querySelector?.(SECTION_WHOLE_SELECTORS)
    );
  }

  function markItems(section) {
    // Adawat's On Fire — ONE section animation only. Never stagger slides/cards.
    if (isOnFireSection(section)) {
      section.querySelectorAll('.adawat-sr-item, .adawat-shoppable-video__slide').forEach((el) => {
        el.classList.remove('adawat-sr-item', 'adawat-sr-item--group');
        el.style.removeProperty('--sr-delay');
      });
      return;
    }

    if (section.querySelector('.adawat-hero')) {
      return;
    }

    if (section.querySelector('.adawat-favorites, .adawat-fav')) {
      section.querySelectorAll('.adawat-fav__slide').forEach((el) => {
        el.classList.remove('adawat-sr-item');
        el.style.removeProperty('--sr-delay');
      });
    }

    const seen = new Set();
    const staggered = [];

    section.querySelectorAll(GROUP_SELECTORS).forEach((el) => {
      if (seen.has(el)) return;
      if (el.closest('.adawat-shoppable-video')) return;
      seen.add(el);
      el.classList.add('adawat-sr-item', 'adawat-sr-item--group');
      el.style.setProperty('--sr-delay', '80');
    });

    section.querySelectorAll(ITEM_SELECTORS).forEach((el) => {
      if (seen.has(el)) return;
      if (el.classList.contains('adawat-sr-item--group')) return;
      if (el.closest('.adawat-sr-item') && el.closest('.adawat-sr-item') !== el) return;
      if (el.closest('.adawat-product-card') || el.closest('.adawat-category-card')) return;
      if (el.closest('.adawat-shoppable-video')) return;
      seen.add(el);
      staggered.push(el);
    });

    staggered.slice(0, MAX_STAGGER_ITEMS).forEach((el, index) => {
      el.classList.add('adawat-sr-item');
      el.style.setProperty('--sr-delay', String(140 + index * ITEM_STAGGER));
    });
  }

  function reveal(el) {
    el.classList.add('is-inview');
  }

  function revealGroup(section) {
    section.querySelectorAll('.adawat-sr-item--group').forEach((item) => reveal(item));
  }

  function init() {
    const main = document.getElementById('MainContent');
    if (!main) return;

    const sections = Array.from(main.querySelectorAll(':scope > .shopify-section')).filter(
      (section) => section.children.length
    );
    if (!sections.length) return;

    const itemObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          itemObserver.unobserve(entry.target);
        });
      },
      { root: null, rootMargin: ROOT_MARGIN, threshold: 0.1 }
    );

    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const section = entry.target;
          reveal(section);
          // Product cards + categories: all at once
          revealGroup(section);
          // Other items: observe individually (staggered)
          section.querySelectorAll('.adawat-sr-item:not(.adawat-sr-item--group)').forEach((item) => {
            itemObserver.observe(item);
          });
          sectionObserver.unobserve(section);
        });
      },
      { root: null, rootMargin: ROOT_MARGIN, threshold: 0.06 }
    );

    sections.forEach((section, sectionIndex) => {
      markItems(section);

      if (sectionIndex === 0) {
        requestAnimationFrame(() => {
          reveal(section);
          revealGroup(section);
          section.querySelectorAll('.adawat-sr-item:not(.adawat-sr-item--group)').forEach((item, i) => {
            window.setTimeout(() => reveal(item), 60 + i * ITEM_STAGGER);
          });
        });
        return;
      }

      sectionObserver.observe(section);
    });

    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target?.closest?.('.shopify-section') || event.target;
      if (!section || !main.contains(section)) return;
      markItems(section);
      reveal(section);
      section.querySelectorAll('.adawat-sr-item').forEach((item) => reveal(item));
    });
  }

  ready(init);
})();
